import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  BookingService,
  GetTransactionSummaryResponse,
  InitializePaymentRequest,
  InitializePaymentResponse,
  PaymentCard,
  GetSessionResponse,
  Campaign,
  Installment,
  BookRequest
} from '../../services/booking.service';
import { SearchService } from '../../services/search.service';
import { HotelService, AddonInfo, AddOnOperation } from '../../services/hotel.service';
import { finalize, catchError, switchMap, tap, take } from 'rxjs/operators';
import { of, throwError, Observable } from 'rxjs';
import { isIsBankasiMaximum } from '../../core/utils/is-bankasi-bins.util';

// Addon seçim durumu interface'i
interface SelectedAddon {
  addonKey: string;
  passengerKey: string;
  name: string;
  price: number;
  isRequired: boolean;
}

import { InstallmentSelectorComponent } from '../../shared/components/installment-selector/installment-selector.component';

@Component({
  selector: 'app-payment',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, InstallmentSelectorComponent],
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.css']
})
export class PaymentComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly bookingService = inject(BookingService);
  private readonly searchService = inject(SearchService);
  private readonly hotelService = inject(HotelService);

  paymentForm!: FormGroup;
  productKey: string = '';
  roomKey: string = '';
  transactionKey: string = '';
  traceId: string = '';
  isLoading = false;
  errorMessage: string | null = null;
  passengerData: any = null;
  searchParams: any = null;
  transactionSummary: GetTransactionSummaryResponse | null = null;
  paymentType: 'partial' | 'full' = 'full';
  
  // Ödeme akışı için gerekli property'ler
  clientSessionKey: string = '';
  availableCampaigns: Campaign[] = [];
  selectedCampaign: Campaign | null = null;
  availableInstallments: Installment[] = [];
  selectedInstallment: Installment | null = null;
  selectedInstallmentCount: number = 1;
  maxInstallment: number = 9; // Kıbrıs: 3, Yurtiçi: 9, Yurtdışı: 0

  isProcessingPayment = false;
  orderId: string = '';  // NestPay OrderId
  private pollingInterval: any = null;

  // Kart son kullanma yılları (mevcut yıldan 15 yıl ilerisi)
  expiryYears = Array.from({ length: 15 }, (_, i) => {
    const year = new Date().getFullYear() + i;
    return { value: String(year).slice(-2), label: String(year) };
  });

  // ========== EK HİZMETLER (ADDONS) ==========
  availableAddons: AddonInfo[] = [];           // Mevcut tüm addonlar
  selectedAddons: Map<string, SelectedAddon> = new Map();  // Seçili addonlar (addonKey -> SelectedAddon)
  isAddonLoading: { [key: string]: boolean } = {};  // Her addon için loading durumu
  addonTotalPrice: number = 0;                 // Addon toplam fiyatı
  currentTotalPrice: number = 0;               // Güncellenmiş toplam fiyat (base + addons)
  showAddonsSection: boolean = true;           // Addon bölümü görünürlüğü

  ngOnInit(): void {
    this.maxInstallment = this.searchService.getMaxInstallmentCount();
    this.initializeForm();
    
    // URL parametrelerini al - ⭐ take(1) ile sadece ilk değeri al (duplicate request önlenir)
    this.route.queryParams.pipe(take(1)).subscribe(params => {
      this.productKey = params['productKey'] || '';
      this.roomKey = params['roomKey'] || '';
      this.transactionKey = params['transactionKey'] || '';
      
      // ✅ FALLBACK: URL'de yoksa SessionStorage'dan al
      if (!this.transactionKey || this.transactionKey.trim() === '') {
        const storedTransactionKey = sessionStorage.getItem('currentTransactionKey');
        if (storedTransactionKey) {
          this.transactionKey = storedTransactionKey;
        } else {
        this.errorMessage = 'İşlem bilgisi bulunamadı. Lütfen ödeme adımını tekrar başlatın.';
        }
      } else {
      }
      
      // ✅ İLK YÜKLEME: Transaction summary çağrısı - Fiyat bilgisi için gerekli
      if (this.transactionKey) {
        this.loadTransactionSummary();
      }
    });

    // ✅ ÖNCE: Navigation state'den almayı dene
    const navigation = this.router.getCurrentNavigation();
    if (navigation?.extras?.state) {
      this.passengerData = navigation.extras.state['passengerData'];
      this.searchParams = navigation.extras.state['searchParams'];
    } 
    // ✅ SONRA: SessionStorage'dan almayı dene
    else {
      
      const storedPassengerData = sessionStorage.getItem('currentPassengerData');
      if (storedPassengerData) {
        try {
          this.passengerData = JSON.parse(storedPassengerData);
        } catch (error) {
        }
      } else {
      }
    }

    // SearchService'den search results'ı al - ⭐ take(1) ile sadece ilk değeri al
    this.searchService.searchResults$.pipe(take(1)).subscribe(results => {
      if (results) {
        this.searchParams = results;
        
        // ✅ Addon'ları search results'tan yükle
        this.loadAddonsFromSearchResults(results);
      }
    });

    // ⚠️ HATA: Passenger data bulunamadı - Mock data KULLANILMAYACAK
    if (!this.passengerData) {
      
      // ❌ Mock data KALDIRILDI - Backend gerçek veri bekliyor
      // Test verileri ("Test User" vb.) Gordios ValidateBooking'de reddedilir
      
      // Kullanıcıya uyarı göster ve yönlendir
      this.errorMessage = 'Yolcu bilgileri eksik. Yolcu bilgileri sayfasına yönlendiriliyorsunuz...';
      this.isLoading = false;
      
      setTimeout(() => {
        this.router.navigate(['/yolcu-bilgileri'], {
          queryParams: {
            productKey: this.productKey,
            roomKey: this.roomKey
          }
        });
      }, 2000);
      
      return; // ⚠️ Önemli: Form işlemlerini durdur
    }
  }

  private initializeForm(): void {
    this.paymentForm = this.fb.group({
      paymentMethod: ['creditCard', [Validators.required]],
      // Kredi kartı alanları
      cardNumber: ['', [Validators.required, Validators.pattern(/^\d{16}$/)]],
      cardHolderName: ['', [Validators.required, Validators.minLength(2)]],
      expiryMonth: ['', [Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])$/)]],
      expiryYear: ['', [Validators.required, Validators.pattern(/^\d{2}$/)]],
      cvv: ['', [Validators.required, Validators.pattern(/^\d{3,4}$/)]],
      installments: [1, Validators.required],
      // Hediye çeki alanları (başlangıçta required değil)
      giftCardCode: [''],
      // Banka havalesi alanları (başlangıçta required değil)
      bankAccountHolder: [''],
      bankAccountNumber: [''],
      bankName: ['']
    });
    
    // Sayfa açıldığında seçili ödeme yöntemine göre validasyonları ayarla
    this.onPaymentMethodChange();
  }

  /**
   * Transaction özetini yükler - Sayfa açıldığında fiyat bilgisi için çağrılır
   */
  private loadTransactionSummary(): void {
    this.isLoading = true;
    this.errorMessage = null;

    this.bookingService.getTransactionSummary(this.transactionKey)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        catchError(error => {
          this.errorMessage = 'Fiyat bilgisi yüklenirken bir sorun oluştu. Lütfen sayfayı yenileyin.';
          return of(null);
        })
      )
      .subscribe({
        next: (response: GetTransactionSummaryResponse | null) => {
          if (response && response.success && response.data) {
            this.transactionSummary = response;
          } else {
            // Hata mesajı gösterme - sadece log'la (fiyat gelirse gösterilir, gelmezse selectedHotel.price kullanılır)
          }
        },
        error: (error) => {
        }
      });
  }

  onSubmit(): void {
    // Çift tıklamayı engelle – tek bir initialize isteği garanti et (3D hata önlemi)
    if (this.isProcessingPayment || this.isLoading) {
      return;
    }
    this.isLoading = true;

    // 1. Temel kontroller
    if (!this.canProceedToPayment) {
      this.isLoading = false;
      this.errorMessage = 'Lütfen tüm alanları doğru şekilde doldurunuz.';
      this.markFormGroupTouched(this.paymentForm);
      return;
    }

    if (!this.paymentForm.valid || !this.passengerData) {
      this.markFormGroupTouched(this.paymentForm);
      this.errorMessage = 'Lütfen tüm alanları doğru şekilde doldurunuz.';
      this.isLoading = false;
      return;
    }

    // ✅ YENİ: Yolcu verilerini validate et (test verileri, master passenger kontrolü)
    const validationError = this.validatePassengerData();
    if (validationError) {
      this.errorMessage = validationError;
      this.isLoading = false;
      return;
    }

    // 2. ✅ ŞİMDİ transaction summary çek ve backend validation yap
    this.errorMessage = null;

    this.bookingService.getTransactionSummary(this.transactionKey)
      .pipe(
        finalize(() => {
          // isLoading'i processBooking içinde yönetiyoruz
        })
      )
      .subscribe({
        next: (summary) => {
          // ✅ TAM RESPONSE'U GÖSTER
          
          if (!summary || !summary.success || !summary.data) {
            this.isLoading = false;
            this.errorMessage = 'Ödeme bilgileri yüklenemedi. Lütfen tekrar deneyin.';
            return;
          }

          // ✅ SADECE BALANCE KONTROLÜ
          // isBookable kontrolü KALDIRILDI - Backend callback'te Book yaparken kontrol edecek
          const balance = summary.data.balance?.value || 0;
          const totalPayment = summary.data.totalPayment?.value || 0;
          

          if (balance <= 0) {
            this.isLoading = false;
            this.errorMessage = 'Bu işlem zaten tamamlanmış veya ödeme tutarı geçersiz.';
            return;
          }

          // ✅ Her şey OK - Summary'yi kaydet ve ödeme başlat
          // Backend callback'te otomatik olarak Book API çağrılacak
          
          this.transactionSummary = summary;
          this.processBooking();
        },
        error: (error) => {
          this.isLoading = false;
          this.errorMessage = 'Ödeme işlemi başlatılamadı. Lütfen tekrar deneyin.';
        }
      });
  }

  private processBooking(): void {
    this.isProcessingPayment = true;
    this.isLoading = true;
    this.errorMessage = null;

    // TraceId'yi al (cookie veya search params'tan)
    this.traceId = this.searchParams?.traceId || this.bookingService.getTraceIdFromCookie() || '';
    
    if (!this.traceId) {
      this.errorMessage = 'Oturumunuzun süresi dolmuş. Lütfen yeni bir arama yapın.';
      this.isLoading = false;
      this.isProcessingPayment = false;
      return;
    }


    // Adım 1: GetSession
    this.bookingService.getSession(this.traceId)
      .pipe(
        catchError(error => {
          this.errorMessage = 'Ödeme oturumu oluşturulamadı. Lütfen tekrar deneyin.';
          this.isLoading = false;
          this.isProcessingPayment = false;
          return throwError(() => error);
        }),
        switchMap((sessionResponse: GetSessionResponse) => {
          this.clientSessionKey = sessionResponse.clientSessionKey;
          
          // Adım 2: Ödeme bilgilerini hazırla ve NestPay'e gönder
          return this.initializePaymentProcess();
        })
      )
      .subscribe({
        next: (response) => {
        },
        error: (error) => {
          if (!this.errorMessage) {
            this.errorMessage = 'Ödeme işlemi başlatılamadı. Lütfen bilgilerinizi kontrol edip tekrar deneyin.';
          }
          this.isLoading = false;
          this.isProcessingPayment = false;
        }
      });
  }

  /**
   * NestPay ödeme sürecini başlatır (3D_PAY)
   */
  private buildInvoicePayload(mainPassenger: any): any {
    try {
      const raw = sessionStorage.getItem('currentInvoiceInfo');
      if (raw) {
        const inv = JSON.parse(raw);
        if (inv?.type === 'Corporate') {
          return {
            invoiceType: 'CORPORATE',
            companyName: inv.companyName || '',
            taxOffice: inv.taxOffice || '',
            taxNumber: inv.taxNumber || '',
            companyAddress: inv.companyAddress || '',
            companyCity: inv.companyCity || '',
            companyPostalCode: inv.companyPostalCode || '',
            companyPhoneNumber: inv.companyPhoneNumber || '',
            companyEmail: inv.companyEmail || ''
          };
        }
      }
    } catch {}
    // Varsayılan bireysel — ana yolcunun kimlik bilgileri
    return {
      invoiceType: 'PERSONAL',
      tcNumber: mainPassenger?.tcKimlikNo || '',
      personalName: mainPassenger?.name || '',
      personalSurname: mainPassenger?.surname || ''
    };
  }

  private initializePaymentProcess(): Observable<any> {
    const paymentValue = this.paymentForm.value;
    const paymentAmount = this.getPaymentAmount();
    
    const mainPassenger = this.mainPassenger || this.passengerData?.passengers?.[0];
    if (!mainPassenger) {
      this.errorMessage = 'Yolcu bilgisi bulunamadı.';
      this.isLoading = false;
      this.isProcessingPayment = false;
      return of(null);
    }

    // PaymentCard bilgilerini hazırla (NestPay formatı)
    const cardNumber = paymentValue.cardNumber.replace(/\s/g, '');
    const paymentCard: PaymentCard = {
      cardHolderName: paymentValue.cardHolderName,
      cardNumber: cardNumber,
      expireMonth: paymentValue.expiryMonth,
      expireYear: paymentValue.expiryYear,
      cvc: paymentValue.cvv,
      cardType: this.detectCardType(cardNumber)  // "1" = Visa, "2" = MasterCard
    };

    // Check-in/Check-out tarihleri
    const hotel = this.selectedHotel;
    const checkInDate = hotel?.checkIn 
      || this.searchParams?.searchCriteria?.checkInDate 
      || sessionStorage.getItem('checkInDate') 
      || '';
    const checkOutDate = hotel?.checkOut 
      || this.searchParams?.searchCriteria?.checkOutDate 
      || sessionStorage.getItem('checkOutDate') 
      || '';

    // NestPay Initialize Payment Request
    const initPaymentRequest: InitializePaymentRequest = {
      traceId: this.traceId,
      transactionKey: this.transactionKey,
      productKey: this.productKey,
      
      // Yolcu bilgileri - JSON string
      passengerInfo: JSON.stringify(
        this.passengerData.passengers.map((passenger: any) => ({
          key: passenger.key || '',
          name: passenger.name || '',
          surname: passenger.surname || '',
          email: passenger.email || '',
          birthday: passenger.birthday || new Date().toISOString(),
          gender: passenger.gender ?? 0,
          passengerType: passenger.passengerType ?? 0,
          age: passenger.age ?? 35,
          tcKimlikNo: passenger.tcKimlikNo || '',
          isMaster: passenger.isMaster || false,
          addressStreet: passenger.address?.street || null,
          addressPostalCode: passenger.address?.postalCode || null
        }))
      ),
      
      // İletişim bilgileri - JSON string
      contactInfo: JSON.stringify({
        name: mainPassenger.name || '',
        surname: mainPassenger.surname || '',
        email: mainPassenger.email || '',
        phoneNumbers: (mainPassenger.phoneNumbers && mainPassenger.phoneNumbers.length > 0)
          ? mainPassenger.phoneNumbers.map((phone: any) => ({
              countryCode: parseInt(phone.countryCode) || 90,
              areaCode: parseInt(phone.areaCode) || 0,
              number: phone.number || '',
              type: phone.type || 'Mobile'
            }))
          : [{ countryCode: 90, areaCode: 0, number: '', type: 'Mobile' }]
      }),
      
      price: paymentAmount,
      paidPrice: paymentAmount,
      currency: 'TRY',
      installment: this.selectedInstallmentCount || 1,
      paymentCard: paymentCard,
      
      // Opsiyonel otel bilgileri
      hotelName: hotel?.name || '',
      roomName: hotel?.roomType || '',
      checkInDate: checkInDate,
      checkOutDate: checkOutDate,
      ...this.buildInvoicePayload(mainPassenger)
    };

    // Debug log
    
    // TransactionKey kontrolü
    if (!initPaymentRequest.transactionKey || initPaymentRequest.transactionKey.trim() === '') {
      this.errorMessage = 'Transaction anahtarı eksik. Lütfen yolcu bilgileri sayfasından tekrar başlayın.';
      this.isLoading = false;
      this.isProcessingPayment = false;
      
      setTimeout(() => {
        this.router.navigate(['/yolcu-bilgileri'], {
          queryParams: { productKey: this.productKey, roomKey: this.roomKey }
        });
      }, 3000);
      
      return of(null);
    }

    // NestPay ödeme başlat
    return this.bookingService.initializePayment(initPaymentRequest)
      .pipe(
        catchError(error => {
          this.errorMessage = error.message || 'Ödeme başlatılamadı.';
          this.isLoading = false;
          this.isProcessingPayment = false;
          return throwError(() => error);
        }),
        tap((response: InitializePaymentResponse) => {
          
          if (response.success && response.data) {
            
            // OrderId'yi sakla
            this.orderId = response.data.orderId || '';
            
            // GateUrl ve FormParams kontrolü
            if (response.data.gateUrl && response.data.formParams) {
              this.isLoading = false;
              
              // Hidden form oluştur ve banka sayfasına yönlendir
              this.submitTo3DGate(response);
            } else {
              this.errorMessage = '3D Secure bilgileri alınamadı.';
              this.isLoading = false;
              this.isProcessingPayment = false;
            }
          } else {
            this.errorMessage = response.message || 'Ödeme başlatılamadı.';
            this.isLoading = false;
            this.isProcessingPayment = false;
          }
        })
      );
  }

  /**
   * Kart numarasından kart tipini tespit eder
   * @param cardNumber Kart numarası (boşluksuz)
   * @returns "1" = Visa, "2" = MasterCard
   */
  private detectCardType(cardNumber: string): string {
    const cleaned = cardNumber.replace(/\s/g, '');
    if (cleaned.startsWith('4')) return '1';      // Visa
    if (cleaned.startsWith('5')) return '2';      // MasterCard
    if (cleaned.startsWith('2')) return '2';      // MasterCard (2xxx serisi)
    return '1'; // Varsayılan: Visa
  }

  /**
   * NestPay 3D Gate'e hidden form ile yönlendirir
   * Backend'den gelen gateUrl ve formParams ile banka sayfasına auto-submit
   */
  private submitTo3DGate(response: InitializePaymentResponse): void {
    if (!response.data) {
      this.errorMessage = '3D Secure bilgileri eksik.';
      this.isProcessingPayment = false;
      return;
    }


    // Hidden form oluştur
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = response.data.gateUrl;
    form.style.display = 'none';

    // Form parametrelerini ekle
    for (const [key, value] of Object.entries(response.data.formParams)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = value;
      form.appendChild(input);
    }

    // Form'u sayfaya ekle ve submit et
    document.body.appendChild(form);
    
    form.submit();
  }

  /**
   * İyzico 3D Secure HTML içeriğini direkt render eder
   * Form otomatik submit olacak ve kullanıcı İyzico sayfasına yönlendirilecek
   */
  /**
   * Ödeme callback'ini dinler (NestPay için)
   * Backend callback sonrası otomatik yönlendirme yapar
   */
  private listenForPaymentCallback(): void {
  }

  /**
   * ❌ DEPRECATED: Transaction durumunu polling ile kontrol eder
   * Backend artık otomatik callback yapıyor, polling'e gerek yok
   */
  private startPaymentPolling(): void {
    // ❌ POLLING MEKANIZMASI KALDIRILDI
    // Backend'in callback mekanizması kullanılıyor
    
    /* ESKI KOD - Yedek için yorumda
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
    }

    let pollCount = 0;
    const maxPolls = 30;
    const pollInterval = 10000;
    
    this.checkTransactionStatus();
    
    this.pollingInterval = setInterval(() => {
      pollCount++;
      
      if (pollCount >= maxPolls) {
        this.stopPaymentPolling();
        this.handlePaymentTimeout();
        return;
      }
      
      if (!this.isProcessingPayment) {
        this.stopPaymentPolling();
        return;
      }
      
      this.checkTransactionStatus();
      
    }, pollInterval);
    */
  }

  /**
   * ❌ DEPRECATED: Transaction durumunu kontrol eder
   * Artık kullanılmıyor - Backend callback ile hallediyor
   */
  private checkTransactionStatus(): void {
    
    /* ESKI KOD - Yedek için yorumda
    this.bookingService.getTransactionSummary(this.transactionKey)
      .subscribe({
        next: (response) => {
          if (response.success && response.data) {
            const transactionData = response.data as any;
            
            if (transactionData.bookingNumber || transactionData.reservationCode) {
              this.stopPaymentPolling();
              this.handlePaymentSuccess({ transactionData: response.data });
            } 
            else if (response.data.isCompleted === true) {
              this.stopPaymentPolling();
              this.handlePaymentSuccess({ transactionData: response.data });
            }
          }
        },
        error: (error) => {
        }
      });
    */
  }

  /**
   * Polling'i durdur
   */
  private stopPaymentPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  ngOnDestroy(): void {
    // Polling interval'i temizle
    this.stopPaymentPolling();
  }

  /**
   * Ödeme timeout durumunu handle eder
   */
  private handlePaymentTimeout(): void {
    this.isLoading = false;
    this.isProcessingPayment = false;
    this.errorMessage = 'Ödeme işlemi zaman aşımına uğradı. Lütfen tekrar deneyiniz.';
  }

  /**
   * Ödeme başarılı olduğunda çağrılır
   * Backend'de zaten Complete Payment, Book API ve EndTransaction yapılmış olmalı
   */
  private handlePaymentSuccess(data: any): void {
    this.isLoading = true;

    // Backend'in callback endpoint'i zaten ödemeyi tamamlamış, rezervasyon yapmış ve transaction'ı kapattı
    // Şimdi sadece transaction summary'den booking number'ı alalım
    this.bookingService.getTransactionSummary(this.transactionKey)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.isProcessingPayment = false;
        }),
        catchError(error => {
          this.errorMessage = 'Ödeme başarılı ancak rezervasyon bilgileri alınamadı. Lütfen müşteri hizmetleri ile iletişime geçiniz.';
          return of(null);
        })
      )
      .subscribe({
        next: (response) => {
          if (response && response.success && response.data) {
            
            // Booking number veya reservation code varsa (any casting ile optional field'lara eriş)
            const transactionData = response.data as any;
            const bookingNumber = transactionData.bookingNumber || transactionData.reservationCode;
            
            if (bookingNumber) {
              // Onay sayfasına yönlendir
              this.router.navigate(['/rezervasyon-onay'], {
                queryParams: {
                  bookingNumber: bookingNumber,
                  transactionKey: this.transactionKey,
                  traceId: this.traceId,
                  orderId: this.orderId
                },
                state: {
                  transactionData: response.data,
                  paymentSuccess: true
                }
              });
            } else {
              // Booking number yoksa, genel başarı mesajı göster
              this.router.navigate(['/rezervasyon-onay'], {
                queryParams: {
                  transactionKey: this.transactionKey,
                  success: 'true'
                },
                state: {
                  transactionData: response.data,
                  paymentSuccess: true
                }
              });
            }
          } else {
            this.errorMessage = 'Rezervasyon bilgileri alınamadı. Lütfen müşteri hizmetleri ile iletişime geçiniz.';
          }
        },
        error: (error) => {
          this.errorMessage = 'Ödeme başarılı ancak rezervasyon bilgileri alınamadı. Lütfen müşteri hizmetleri ile iletişime geçiniz.';
        }
      });
  }

  /**
   * Ödeme başarısız olduğunda çağrılır
   */
  private handlePaymentFailed(data: any): void {
    this.isLoading = false;
    this.isProcessingPayment = false;
    this.errorMessage = data.message || 'Ödeme işlemi başarısız oldu. Lütfen kartınızı kontrol edip tekrar deneyiniz.';
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control?.markAsTouched();
    });
  }

  // CVV formatla
  formatCVV(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    value = value.substring(0, 4);
    event.target.value = value;
  }

  // Ay formatla
  formatMonth(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    if (value.length === 1 && parseInt(value) > 1) {
      value = '0' + value;
    }
    value = value.substring(0, 2);
    event.target.value = value;
  }

  // Yıl formatla
  formatYear(event: any): void {
    let value = event.target.value.replace(/\D/g, '');
    value = value.substring(0, 2);
    event.target.value = value;
  }

  goBack(): void {
    this.router.navigate(['/yolcu-bilgileri'], {
      queryParams: {
        productKey: this.productKey,
        roomKey: this.roomKey
      }
    });
  }

  get totalPassengers(): number {
    return this.passengerData?.passengers?.length || 0;
  }

  get mainPassenger(): any {
    return this.passengerData?.passengers?.find((p: any) => p.isMaster);
  }

  get canProceedToPayment(): boolean {
    // ✅ BASIT KONTROL: Transaction summary'ye gerek yok
    // Backend validation submit edildiğinde yapılacak
    
    const hasForm = !!this.paymentForm;
    const formValid = this.paymentForm?.valid || false;
    const hasTransactionKey = !!(this.transactionKey && this.transactionKey.trim());
    const hasProductKey = !!(this.productKey && this.productKey.trim());
    
    
    return hasForm && formValid && hasTransactionKey && hasProductKey;
  }
  
  // Form validasyon hatalarını döndür
  private getFormValidationErrors(): any {
    const errors: any = {};
    if (!this.paymentForm) return errors;
    
    Object.keys(this.paymentForm.controls).forEach(key => {
      const control = this.paymentForm.get(key);
      if (control && control.errors) {
        errors[key] = control.errors;
      }
    });
    return errors;
  }

  /**
   * ✅ YENİ: Yolcu verilerini validate eder
   * Backend'e göndermeden önce test verileri ve zorunlu alan kontrolü yapar
   * @returns string | null - Hata mesajı veya null (başarılı)
   */
  private validatePassengerData(): string | null {
    if (!this.passengerData?.passengers || this.passengerData.passengers.length === 0) {
      return 'Yolcu bilgileri bulunamadı. Lütfen yolcu bilgileri sayfasından tekrar başlayın.';
    }

    const passengers = this.passengerData.passengers;

    // 1. ✅ Master passenger kontrolü - Sadece 1 tane olmalı
    const masterPassengers = passengers.filter((p: any) => p.isMaster === true);
    if (masterPassengers.length === 0) {
      return 'Ana yolcu (master passenger) belirlenmemiş. Lütfen yolcu bilgilerini kontrol edin.';
    }
    if (masterPassengers.length > 1) {
      return 'Birden fazla ana yolcu belirlenmis. Sadece 1 ana yolcu olmalıdır.';
    }

    const master = masterPassengers[0];

    // 2. ✅ Master passenger zorunlu alanları
    if (!master.email || master.email.trim() === '') {
      return 'Ana yolcu için e-posta adresi zorunludur.';
    }
    if (!master.address?.street || master.address.street.trim() === '') {
      return 'Ana yolcu için adres bilgisi zorunludur.';
    }
    if (!master.address?.postalCode || master.address.postalCode.trim() === '') {
      return 'Ana yolcu için posta kodu zorunludur.';
    }

    // 3. ✅ TC Kimlik No kontrolü - Tüm yolcular için
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i];
      if (!p.tcKimlikNo || p.tcKimlikNo.length !== 11) {
        return `${i + 1}. yolcu için geçerli TC Kimlik No gerekli (11 haneli).`;
      }
    }

    // 3.5. ⭐ KRİTİK: PassengerKey kontrolü - Search'ten gelen key ZORUNLU
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i];
      if (!p.key || p.key.trim() === '') {
        // Not: Uyarı ver ama engelleme - backend fallback yapabilir
      }
    }

    // 4. ✅ Telefon numarası kontrolü - Master passenger için
    if (!master.phoneNumbers || master.phoneNumbers.length === 0) {
      return 'Ana yolcu için telefon numarası zorunludur.';
    }
    const firstPhone = master.phoneNumbers[0];
    if (!firstPhone.number || firstPhone.number.trim() === '') {
      return 'Ana yolcu için geçerli telefon numarası zorunludur.';
    }

    // 5. ✅ Test verisi kontrolü - Gordios bu verileri reddeder
    // ⚠️ NOT: "deed" kaldırıldı - gerçek isimlerde geçebilir (örn: Deedeoğlu)
    const exactTestNames = ['test', 'test user', 'dummy', 'sample', 'fake', 'user'];
    const substringPatterns = ['deneme', 'örnek']; // Türkçe test kalıpları
    
    for (let i = 0; i < passengers.length; i++) {
      const p = passengers[i];
      const name = (p.name || '').toLowerCase().trim();
      const surname = (p.surname || '').toLowerCase().trim();
      const fullName = `${name} ${surname}`;
      
      // Tam eşleşme kontrolü (isim veya soyisim)
      if (exactTestNames.includes(name) || exactTestNames.includes(surname) || exactTestNames.includes(fullName)) {
        return `${i + 1}. yolcu için test verisi kullanılamaz. Lütfen gerçek müşteri bilgilerini girin.`;
      }
      
      // Substring kontrolü (sadece Türkçe patternler)
      for (const pattern of substringPatterns) {
        if (fullName.includes(pattern)) {
          return `${i + 1}. yolcu için test verisi kullanılamaz. Lütfen gerçek müşteri bilgilerini girin.`;
        }
      }
    }

    // 6. ✅ Email formatı kontrolü (basit)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(master.email)) {
      return 'Ana yolcu için geçerli bir e-posta adresi girin.';
    }


    return null; // ✅ Başarılı
  }

  /**
   * ❌ DEPRECATED: Backend'in isBookable değeri Book API için kullanılıyor
   * Frontend ödeme başlatmak için balance kontrolü yapmalı
   * @deprecated Artık kullanılmıyor - Balance kontrolü yeterli
   */
  get isBookable(): boolean {
    return this.transactionSummary?.data?.isBookable === true;
  }

  get payment3DIsRequired(): boolean {
    return this.transactionSummary?.data?.payment3DIsRequired === true;
  }

  get basketLeastwiseAmount(): number {
    return this.transactionSummary?.data?.basketLeastwiseAmount?.value || 0;
  }

  get balance(): number {
    return this.transactionSummary?.data?.balance?.value || 0;
  }

  get scopePaymentTypes(): (number | string)[] {
    return this.transactionSummary?.data?.scopePaymentTypes || [];
  }

  get selectedHotel(): any {
    if (!this.searchParams || !this.productKey) {
      return null;
    }

    // Search results'tan hotel bilgilerini al
    const searchResults = this.searchParams;
    
    // ProductKey ile eşleşen item'ı bul
    let selectedItem = searchResults?.items?.find((item: any) => 
      item.productKey === this.productKey || 
      item.productkey === this.productKey ||
      item.key === this.productKey ||
      item.id === this.productKey
    );
    
    // Eğer bulunamazsa, ilk item'ı kullan (genelde tek hotel seçimi olur)
    if (!selectedItem && searchResults?.items?.length > 0) {
      selectedItem = searchResults.items[0];
    }
    
    if (!selectedItem) {
      return null;
    }

    // Hotel bilgilerini al (includeDetails kaldırıldığı için boş gelebilir)
    const hotelData = searchResults?.hotels?.[selectedItem.hotelKey];

    return {
      name: hotelData?.name || '',
      location: hotelData?.address || '',
      checkIn: selectedItem.checkInDate,
      checkOut: selectedItem.checkOutDate,
      checkInTime: hotelData?.checkIn || '14:00',
      checkOutTime: hotelData?.checkOut || '12:00',
      roomType: selectedItem.rooms?.[0]?.accomodationType || 'Standart',
      mealType: selectedItem.rooms?.[0]?.mealType || 'Her Şey Dahil',
      stars: hotelData?.hotelCategoryId || 5,
      rating: hotelData?.hotelPoint || 0,
      image: this.toThumbnail(hotelData?.images?.[0]?.url || hotelData?.thumbnailUrl) || 'assets/img/hotels/default.png',
      price: selectedItem.basePrice?.value || selectedItem.totalPrice?.value || 0
    };
  }

  onPaymentMethodChange(): void {
    const paymentMethod = this.paymentForm.get('paymentMethod')?.value;
    
    // Tüm alanları temizle
    this.paymentForm.patchValue({
      cardNumber: '',
      cardHolderName: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      installments: 1,
      giftCardCode: '',
      bankAccountHolder: '',
      bankAccountNumber: '',
      bankName: ''
    });
    
    // Tüm validasyonları kaldır
    this.paymentForm.get('cardNumber')?.clearValidators();
    this.paymentForm.get('cardHolderName')?.clearValidators();
    this.paymentForm.get('expiryMonth')?.clearValidators();
    this.paymentForm.get('expiryYear')?.clearValidators();
    this.paymentForm.get('cvv')?.clearValidators();
    this.paymentForm.get('giftCardCode')?.clearValidators();
    this.paymentForm.get('bankAccountHolder')?.clearValidators();
    this.paymentForm.get('bankAccountNumber')?.clearValidators();
    this.paymentForm.get('bankName')?.clearValidators();
    
    // Seçilen ödeme yöntemine göre validasyonları ekle
    if (paymentMethod === 'creditCard') {
      this.paymentForm.get('cardNumber')?.setValidators([Validators.required, Validators.pattern(/^\d{16}$/)]);
      this.paymentForm.get('cardHolderName')?.setValidators([Validators.required, Validators.minLength(2)]);
      this.paymentForm.get('expiryMonth')?.setValidators([Validators.required, Validators.pattern(/^(0[1-9]|1[0-2])$/)]);
      this.paymentForm.get('expiryYear')?.setValidators([Validators.required, Validators.pattern(/^\d{2}$/)]);
      this.paymentForm.get('cvv')?.setValidators([Validators.required, Validators.pattern(/^\d{3,4}$/)]);
    } else if (paymentMethod === 'giftCard') {
      this.paymentForm.get('giftCardCode')?.setValidators([Validators.required, Validators.minLength(6)]);
    } else if (paymentMethod === 'bankTransfer') {
      this.paymentForm.get('bankAccountHolder')?.setValidators([Validators.required, Validators.minLength(2)]);
      this.paymentForm.get('bankAccountNumber')?.setValidators([Validators.required, Validators.minLength(10)]);
      this.paymentForm.get('bankName')?.setValidators([Validators.required, Validators.minLength(2)]);
    }
    
    // Form'u güncelle
    this.paymentForm.get('cardNumber')?.updateValueAndValidity();
    this.paymentForm.get('cardHolderName')?.updateValueAndValidity();
    this.paymentForm.get('expiryMonth')?.updateValueAndValidity();
    this.paymentForm.get('expiryYear')?.updateValueAndValidity();
    this.paymentForm.get('cvv')?.updateValueAndValidity();
    this.paymentForm.get('giftCardCode')?.updateValueAndValidity();
    this.paymentForm.get('bankAccountHolder')?.updateValueAndValidity();
    this.paymentForm.get('bankAccountNumber')?.updateValueAndValidity();
    this.paymentForm.get('bankName')?.updateValueAndValidity();
  }

  get isCreditCardSelected(): boolean {
    return this.paymentForm.get('paymentMethod')?.value === 'creditCard';
  }

  get isGiftCardSelected(): boolean {
    return this.paymentForm.get('paymentMethod')?.value === 'giftCard';
  }

  get isBankTransferSelected(): boolean {
    return this.paymentForm.get('paymentMethod')?.value === 'bankTransfer';
  }

  get availablePaymentMethods(): any[] {
    return [
      { 
        value: 'creditCard', 
        label: 'Kredi Kartı', 
        description: 'Visa, Mastercard, American Express',
        icon: 'icon-credit-card',
        color: 'text-blue-1'
      },
      { 
        value: 'giftCard', 
        label: 'Hediye Çeki', 
        description: 'Hediye çeki kodu ile ödeme',
        icon: 'icon-gift',
        color: 'text-purple-1'
      },
      { 
        value: 'bankTransfer', 
        label: 'Banka/Havale', 
        description: 'Banka havalesi ile ödeme',
        icon: 'icon-bank',
        color: 'text-orange-1'
      }
    ];
  }

  getPaymentTypesText(paymentTypes: (number | string)[]): string {
    const paymentTypeMap: { [key: number]: string } = {
      1: 'Kredi Kartı',
      2: 'Banka Kartı',
      3: 'Nakit',
      4: 'Havale',
      5: 'EFT',
      6: 'Çek',
      8: 'Mobil Ödeme',
      10: 'Dijital Cüzdan',
      13: 'Kripto Para',
      14: 'Bitcoin',
      15: 'Ethereum',
      16: 'Litecoin',
      17: 'Ripple',
      22: 'Diğer'
    };

    return paymentTypes
      .map(type => paymentTypeMap[Number(type)] || `Tip ${type}`)
      .join(', ');
  }

  // Ödeme tipi seçimi
  selectPaymentType(type: 'partial' | 'full'): void {
    this.paymentType = type;
  }

  // Ödeme butonu metni
  getPaymentButtonText(): string {
    if (this.paymentType === 'partial') {
      const amount = this.transactionSummary?.data?.basketLeastwiseAmount?.value || 0;
      return `Kısmi Ödeme Yap (${amount.toLocaleString('tr-TR')} TL)`;
    } else {
      const amount = this.transactionSummary?.data?.balance?.value || 0;
      return `Tam Ödeme Yap (${amount.toLocaleString('tr-TR')} TL)`;
    }
  }

  // Ödeme tutarını hesapla
  getPaymentAmount(): number {
    if (this.paymentType === 'partial') {
      return this.transactionSummary?.data?.basketLeastwiseAmount?.value || 0;
    } else {
      return this.transactionSummary?.data?.balance?.value || 0;
    }
  }

  // Gece sayısını hesapla
  getNightCount(): number {
    if (!this.selectedHotel?.checkIn || !this.selectedHotel?.checkOut) {
      return 1; // Varsayılan değer
    }

    const checkIn = new Date(this.selectedHotel.checkIn);
    const checkOut = new Date(this.selectedHotel.checkOut);
    
    // Tarih farkını hesapla (milisaniye cinsinden)
    const timeDiff = checkOut.getTime() - checkIn.getTime();
    
    // Milisaniyeyi güne çevir
    const nightCount = Math.ceil(timeDiff / (1000 * 3600 * 24));
    
    return nightCount > 0 ? nightCount : 1;
  }

  // Oda sayısını al
  getRoomCount(): number {
    // Search parametrelerinden oda sayısını al
    if (this.searchParams?.searchCriteria?.rooms) {
      return this.searchParams.searchCriteria.rooms;
    }
    
    // Eğer searchParams yoksa, passengerData'dan çıkar
    if (this.passengerData?.passengers?.length) {
      // Genelde her 2 yetişkin için 1 oda kullanılır
      const adultCount = this.passengerData.passengers.length;
      return Math.ceil(adultCount / 2);
    }
    
    return 1; // Varsayılan değer
  }

  // Dinamik oda ve gece metni
  getRoomNightText(): string {
    const roomCount = this.getRoomCount();
    const nightCount = this.getNightCount();
    
    const roomText = roomCount === 1 ? 'ODA' : 'ODA';
    const nightText = nightCount === 1 ? 'GECE' : 'GECE';
    
    return `${roomCount} ${roomText}, ${nightCount} ${nightText} TOPLAM`;
  }

  // ========== EK HİZMETLER (ADDONS) METODLARI ==========

  /**
   * Search results'tan addon'ları yükler
   * @param searchResults Search API'den gelen response
   */
  private loadAddonsFromSearchResults(searchResults: any): void {
    if (!searchResults || !this.productKey) {
      return;
    }

    // İlgili oteli bul
    const hotelItem = searchResults.items?.find((item: any) => item.key === this.productKey);
    if (!hotelItem) {
      return;
    }

    // Hotel bilgilerinden addon'ları al
    const hotelData = searchResults.hotels?.[hotelItem.hotelKey];
    const addons = hotelData?.addons || [];
    
    // Item içindeki addon'ları da kontrol et
    const itemAddons = hotelItem.addons || [];
    
    // Tüm addon'ları birleştir (tekrarları engelle)
    const allAddons = [...addons, ...itemAddons];
    const uniqueAddons = allAddons.filter((addon: any, index: number, self: any[]) => 
      index === self.findIndex((a: any) => a.key === addon.key)
    );

    this.availableAddons = uniqueAddons.map((addon: any) => ({
      key: addon.key || addon.addonKey,
      id: addon.id,
      name: addon.name || addon.title || 'Ek Hizmet',
      description: addon.description || addon.text || '',
      price: addon.price || addon.amount || { value: 0, currency: 949 },
      isRequired: addon.isRequired || addon.isMandatory || false,
      isSelected: addon.isRequired || false,
      addonType: addon.type || addon.addonType || 'General',
      perPerson: addon.perPerson || false,
      perNight: addon.perNight || false
    }));


    // Zorunlu addon'ları otomatik seç
    this.initializeRequiredAddons();
    
    // Toplam fiyatı güncelle
    this.calculateAddonTotalPrice();
  }

  /**
   * Zorunlu addon'ları otomatik olarak seçer
   */
  private initializeRequiredAddons(): void {
    if (!this.passengerData?.passengers?.length) {
      return;
    }

    const masterPassenger = this.passengerData.passengers.find((p: any) => p.isMaster) 
      || this.passengerData.passengers[0];

    this.availableAddons
      .filter(addon => addon.isRequired)
      .forEach(addon => {
        const selectedAddon: SelectedAddon = {
          addonKey: addon.key,
          passengerKey: masterPassenger.key || 'master',
          name: addon.name,
          price: addon.price?.value || 0,
          isRequired: true
        };
        this.selectedAddons.set(addon.key, selectedAddon);
        addon.isSelected = true;
      });

  }

  /**
   * Addon toggle işlemi
   * @param addon Toggle edilecek addon
   * @param event Checkbox event
   */
  toggleAddon(addon: AddonInfo, event: any): void {
    // Zorunlu addon'lar değiştirilemez
    if (addon.isRequired) {
      event.preventDefault();
      return;
    }

    const isSelected = event.target.checked;
    const addonKey = addon.key;

    // ✅ v3.0: TÜM yolcular için addon ekle/çıkar
    const allPassengers = this.passengerData?.passengers || [];
    
    if (!allPassengers || allPassengers.length === 0) {
      this.errorMessage = 'Yolcu bilgisi bulunamadı. Lütfen sayfayı yenileyiniz.';
      return;
    }

    const passengerKeys = allPassengers.map((p: any) => p.key || 'master');

    // Loading durumunu başlat
    this.isAddonLoading[addonKey] = true;

    if (isSelected) {
      // Addon ekle (tüm yolcular için)
      this.addAddonToSelection(addon, passengerKeys);
    } else {
      // Addon çıkar (tüm yolcular için)
      this.removeAddonFromSelection(addon, passengerKeys);
    }
  }

  /**
   * Addon'u seçime ekler ve API'yi çağırır (Çoklu yolcu desteği)
   * @param addon Eklenecek addon
   * @param passengerKeys Yolcu anahtarları array'i
   */
  private addAddonToSelection(addon: AddonInfo, passengerKeys: string[]): void {

    // Her yolcu için operation oluştur
    const operations = passengerKeys.map(pk => ({
      addOnKey: addon.key,
      passengerKey: pk,
      type: 'Add' as const
    }));

    this.hotelService.getDetailWithOperations(this.productKey, operations)
      .pipe(
        finalize(() => {
          this.isAddonLoading[addon.key] = false;
        }),
        catchError(error => {
          this.errorMessage = 'Ek hizmet eklenirken bir sorun oluştu. Lütfen tekrar deneyin.';
          return of(null);
        })
      )
      .subscribe(response => {
        if (response && response.success !== false) {
          // ⭐ ZORUNLU: ProductKey'i güncelle
          if (response.productKey) {
            this.productKey = response.productKey;
          }
          
          // Tüm yolcular için seçimi güncelle
          passengerKeys.forEach(pk => {
            const selectedAddon: SelectedAddon = {
              addonKey: addon.key,
              passengerKey: pk,
              name: addon.name,
              price: addon.price?.value || 0,
              isRequired: addon.isRequired
            };
            this.selectedAddons.set(`${addon.key}_${pk}`, selectedAddon);
          });
          addon.isSelected = true;

          // Yeni fiyatı response'dan al veya hesapla
          // v3.0: totalPrice, basketLeastwiseAmount veya balance'dan al
          let updatedPrice: number | null = null;
          
          if (response.totalPrice) {
            updatedPrice = typeof response.totalPrice === 'number' 
              ? response.totalPrice 
              : response.totalPrice.value;
          } else if (response.basketLeastwiseAmount) {
            updatedPrice = typeof response.basketLeastwiseAmount === 'number'
              ? response.basketLeastwiseAmount
              : response.basketLeastwiseAmount.value;
          } else if (response.balance) {
            updatedPrice = typeof response.balance === 'number'
              ? response.balance
              : response.balance.value;
          }
          
          if (updatedPrice) {
            this.currentTotalPrice = updatedPrice;
          } else {
            this.calculateAddonTotalPrice();
          }

        } else {
          // Hata durumunda checkbox'ı eski haline getir
          addon.isSelected = false;
          this.errorMessage = 'Ek hizmet eklenirken bir sorun oluştu. Lütfen tekrar deneyin.';
        }
      });
  }

  /**
   * Addon'u seçimden çıkarır ve API'yi çağırır (Çoklu yolcu desteği)
   * @param addon Çıkarılacak addon
   * @param passengerKeys Yolcu anahtarları array'i
   */
  private removeAddonFromSelection(addon: AddonInfo, passengerKeys: string[]): void {

    // Her yolcu için operation oluştur
    const operations = passengerKeys.map(pk => ({
      addOnKey: addon.key,
      passengerKey: pk,
      type: 'Remove' as const
    }));

    this.hotelService.getDetailWithOperations(this.productKey, operations)
      .pipe(
        finalize(() => {
          this.isAddonLoading[addon.key] = false;
        }),
        catchError(error => {
          this.errorMessage = 'Ek hizmet kaldırılırken bir sorun oluştu. Lütfen tekrar deneyin.';
          return of(null);
        })
      )
      .subscribe(response => {
        if (response && response.success !== false) {
          // ⭐ ZORUNLU: ProductKey'i güncelle
          if (response.productKey) {
            this.productKey = response.productKey;
          }
          
          // Tüm yolcular için seçimi çıkar
          passengerKeys.forEach(pk => {
            this.selectedAddons.delete(`${addon.key}_${pk}`);
          });
          addon.isSelected = false;

          // Yeni fiyatı response'dan al veya hesapla
          // v3.0: totalPrice, basketLeastwiseAmount veya balance'dan al
          let updatedPrice: number | null = null;
          
          if (response.totalPrice) {
            updatedPrice = typeof response.totalPrice === 'number' 
              ? response.totalPrice 
              : response.totalPrice.value;
          } else if (response.basketLeastwiseAmount) {
            updatedPrice = typeof response.basketLeastwiseAmount === 'number'
              ? response.basketLeastwiseAmount
              : response.basketLeastwiseAmount.value;
          } else if (response.balance) {
            updatedPrice = typeof response.balance === 'number'
              ? response.balance
              : response.balance.value;
          }
          
          if (updatedPrice) {
            this.currentTotalPrice = updatedPrice;
          } else {
            this.calculateAddonTotalPrice();
          }

        } else {
          // Hata durumunda checkbox'ı eski haline getir
          addon.isSelected = true;
          this.errorMessage = 'Ek hizmet kaldırılırken bir sorun oluştu. Lütfen tekrar deneyin.';
        }
      });
  }

  /**
   * Addon toplam fiyatını hesaplar
   */
  private calculateAddonTotalPrice(): void {
    let addonTotal = 0;
    
    this.selectedAddons.forEach(selectedAddon => {
      addonTotal += selectedAddon.price;
    });

    this.addonTotalPrice = addonTotal;

    // Base fiyat + addon fiyatları
    const basePrice = this.selectedHotel?.price || this.transactionSummary?.data?.balance?.value || 0;
    this.currentTotalPrice = basePrice + addonTotal;

  }

  /**
   * Addon'un seçili olup olmadığını kontrol eder
   * @param addonKey Addon anahtarı
   * @returns boolean
   */
  isAddonSelected(addonKey: string): boolean {
    return this.selectedAddons.has(addonKey);
  }

  /**
   * Addon'un loading durumunu kontrol eder
   * @param addonKey Addon anahtarı
   * @returns boolean
   */
  isAddonInProgress(addonKey: string): boolean {
    return this.isAddonLoading[addonKey] || false;
  }

  /**
   * Addon fiyatını formatlar
   * @param addon Addon bilgisi
   * @returns Formatlanmış fiyat string'i
   */
  getAddonPriceText(addon: AddonInfo): string {
    const price = addon.price?.value || 0;
    
    if (price === 0) {
      return 'Ücretsiz';
    }

    let priceText = `+${price.toLocaleString('tr-TR')} ₺`;
    
    // Per person / per night bilgisi
    if (addon.perPerson && addon.perNight) {
      priceText += ' / kişi / gece';
    } else if (addon.perPerson) {
      priceText += ' / kişi';
    } else if (addon.perNight) {
      priceText += ' / gece';
    }

    return priceText;
  }

  /**
   * Addon bölümünü toggle eder
   */
  toggleAddonsSection(): void {
    this.showAddonsSection = !this.showAddonsSection;
  }

  /**
   * Seçili addon sayısını döndürür (zorunlu olanlar hariç)
   */
  get selectedAddonCount(): number {
    let count = 0;
    this.selectedAddons.forEach(addon => {
      if (!addon.isRequired) {
        count++;
      }
    });
    return count;
  }

  /**
   * Opsiyonel addon'ların toplam fiyatını döndürür
   */
  get optionalAddonTotalPrice(): number {
    let total = 0;
    this.selectedAddons.forEach(addon => {
      if (!addon.isRequired) {
        total += addon.price;
      }
    });
    return total;
  }

  /**
   * Tüm yolcular için addon ekler (kişi başı addon'lar için)
   * @param addon Eklenecek addon
   */
  addAddonForAllPassengers(addon: AddonInfo): void {
    if (!addon.perPerson || !this.passengerData?.passengers?.length) {
      return;
    }

    const addonsToAdd = this.passengerData.passengers.map((p: any) => ({
      addonKey: addon.key,
      passengerKey: p.key || 'passenger-' + Math.random().toString(36).substr(2, 6)
    }));

    this.isAddonLoading[addon.key] = true;

    // Operations array oluştur
    const operations = addonsToAdd.map((a: { addonKey: string; passengerKey: string }) => ({ 
      addOnKey: a.addonKey, 
      passengerKey: a.passengerKey, 
      type: 'Add' as const 
    }));
    
    this.hotelService.getDetailWithOperations(this.productKey, operations)
      .pipe(
        finalize(() => {
          this.isAddonLoading[addon.key] = false;
        })
      )
      .subscribe((response: any) => {
        if (response && response.success !== false) {
          // ⭐ ZORUNLU: ProductKey'i güncelle
          if (response.productKey) {
            this.productKey = response.productKey;
          }
          
          // Tüm yolcular için seçimi güncelle
          addonsToAdd.forEach((item: { addonKey: string; passengerKey: string }) => {
            const selectedAddon: SelectedAddon = {
              addonKey: addon.key,
              passengerKey: item.passengerKey,
              name: addon.name,
              price: addon.price?.value || 0,
              isRequired: addon.isRequired
            };
            this.selectedAddons.set(`${addon.key}_${item.passengerKey}`, selectedAddon);
          });
          addon.isSelected = true;

          // v3.0: totalPrice hem number hem object olabilir
          let updatedPrice: number | null = null;
          
          if (response.totalPrice) {
            if (typeof response.totalPrice === 'number') {
              updatedPrice = response.totalPrice;
            } else if (response.totalPrice.value) {
              updatedPrice = response.totalPrice.value;
            }
          } else if (response.basketLeastwiseAmount) {
            updatedPrice = typeof response.basketLeastwiseAmount === 'number'
              ? response.basketLeastwiseAmount
              : response.basketLeastwiseAmount.value;
          } else if (response.balance) {
            updatedPrice = typeof response.balance === 'number'
              ? response.balance
              : response.balance.value;
          }
          
          if (updatedPrice) {
            this.currentTotalPrice = updatedPrice;
          } else {
            this.calculateAddonTotalPrice();
          }
        }
      });
  }

  private toThumbnail(url: string | undefined): string | null {
    if (!url) return null;
    const dotIndex = url.lastIndexOf('.');
    if (dotIndex === -1) return url;
    return url.substring(0, dotIndex) + '-300' + url.substring(dotIndex);
  }
}




